import React, { Component, useMemo } from "react";
import BootstrapTable, { SortOrder, TableChangeState, TableChangeType } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import {
  BasicUserInfo,
  Race,
  RaceDisplayedNames,
  Student,
  StudentInfo,
  StudentsDisplaySettings,
  TeacherInfo,
} from "../../../../../store/onboarding/types";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import {
  assignStudentsToTeacher,
  changeStudentsDisplaySetting,
  getStudentsByInterventionGroup,
  getStudents,
  getTeachers,
  openStudentEditModal,
  showLoadingIndicator,
} from "../../../../../store/onboarding/actions";
import { connect } from "react-redux";
import LoadingIndicator from "../../LoadingIndicator";
import RostersCreateClassModal from "../../../reading-interventions/educators-and-sharing/modals/RostersCreateClassModal";
import { UserAssignmentRole, UserInfo } from "../../../../../store/auth/types";
import Select, { ValueType } from "react-select";
import { getFullName } from "../../../../../utils/NamesUtils";
import { toastr } from "react-redux-toastr";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faEdit, faPen } from "@fortawesome/free-solid-svg-icons";
import { push } from "connected-react-router";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import { hideLoadingIndicator } from "../../../../../store/onboarding/actions";
import { InterventionGroup } from "../../../../../store/onboarding/cases/types";
import _, { set } from "lodash";
import { AxiosError } from "axios";

type PropsFromState = {
  studentsRoster: Array<StudentInfo>;
  teachersRoster: Array<TeacherInfo>;
  studentsByInterventionGroupRoster: Array<any>;
  studentsFilter: string;
  studentsDisplaySettings: StudentsDisplaySettings;
  currentRole?: string;
  errors: {
    getStudents?: string;
    addStudents?: string | Array<any>;
    updateStudentDetails?: string;
    uploadStudentDemographics?: string | Array<any>;
  };
  isLoading: {
    getStudents: boolean;
    addStudents: boolean;
    getTeachers: boolean;
    getStudentsByInterventionGroup: boolean;
    assignStudentsToTeacher: number[];
    isLoadingIndicator: boolean;
    updateStudentDetails: boolean;
  };
  interventionGroups: Array<InterventionGroup>;
  existingStudents: Array<number>;
};

type DispatchProps = {
  getStudents: () => any;
  getTeachers: () => any;
  getStudentsByInterventionGroup: () => any;
  changeStudentsDisplaySetting: (
    studentsDisplaySettings: StudentsDisplaySettings
  ) => any;
  // assignStudentsToTeacher: (
  //   teacherId: number,
  //   students: Array<Student>,
  //   teacherInfo?: UserInfo
  // ) => any;
  push: (path: string) => void;
  openStudentEditModal: (student: Student) => void;
  showLoadingIndicator: () => void;
  hideLoadingIndicator: () => void;
};

type OwnProps = {
  isDistrictOwner?: boolean;
};

type Props = PropsFromState & DispatchProps & OwnProps;

type State = {
  sortedStudentsRoster: Array<StudentInfo>;
  //selectedStudents: Array<StudentInfo>;
  selectedTeacher?: UserInfo;
  showCreateClassModal: boolean;
  sortField: string | undefined;
  sortOrder: 'asc' | 'desc' | undefined;
  columns: any[];
};

// const defaultSorted: [{ dataField: string; order: SortOrder }] = [
//   {
//     dataField: "educator",
//     order: "asc",
//   },
// ];

const options = {
  sizePerPageList: [
    {
      text: "20",
      value: 20,
    },
    {
      text: "50",
      value: 50,
    },
    {
      text: "100",
      value: 100,
    },
  ],
};

function withMyHook(Component:any) {
  return function WrappedComponent(props:Props) {
    const { isTeacher, isDistrictOwner} = useUserRole();
     return <Component {...props} isTeacher={isTeacher} isDistrictOwner={isDistrictOwner} />;
  }
}

class StudentsTable extends Component<Props, State> {
  getColumns = (sortField?: string, sortOrder?: 'asc' | 'desc') => {
    return [
      {
        dataField: "id",
        text: "Student ID",
        hidden: true,
      },
      // {
      //   dataField: "checkbox",
      //   text: "",
      // },
      {
        dataField: 'first_name',
        text: 'First Name',
        sort: true,
      //  sortOrder: sortField === 'first_name' ? sortOrder : undefined, // 🧠 control icon
      },
      {
        dataField: 'last_name',
        text: 'Last Name',
        sort: true,
      //  sortOrder: sortField === 'last_name' ? sortOrder : undefined,
      },
      {
        dataField: "race",
        text: "Race",
        sort: true,
        headerStyle: { width: "20%" }
      },
      {
        dataField: "unique_id",
        text: "Student ID",
        sort: true,
        headerStyle: { width: "12%" }
      },
      {
        dataField: "teachers",
        text: "Teacher(s)",
        sort: true,
        headerStyle: { width: "26%" }
      },
      {
        dataField: "actions",
        text: "Actions",
        sort: false,
        headerStyle: { width: "10%" },
        headerAlign: 'center'
      },
    ];
  }
  
  state: Readonly<State> = {
    selectedTeacher: undefined,
    sortedStudentsRoster: this.props.studentsRoster,
    //selectedStudents: [],
    showCreateClassModal: false,
    sortField: undefined,
    sortOrder: undefined,
    columns: this.getColumns(), // load initial columns
  };

  // Calculate match priority for intelligent sorting (lower number = higher priority)
  getMatchPriority = (student: StudentInfo, searchTerm: string): number => {
    if (!searchTerm) return 0;

    const query = searchTerm.toLowerCase().trim();
    
    // Check Student ID first (exact match only)
    const studentId = student.unique_id?.toString().toLowerCase() || '';
    if (studentId === query) return 1;

    // Searching by name or other fields
    const firstName = student.first_name?.toLowerCase().trim() || '';
    const lastName = student.last_name?.toLowerCase().trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const reverseFullName = `${lastName} ${firstName}`.trim();

    // Priority 1: Exact matches
    if (firstName === query || lastName === query || fullName === query || reverseFullName === query) {
      return 1;
    }

    // Priority 2: Starts with first name or last name
    if (firstName.startsWith(query) || lastName.startsWith(query)) {
      return 2;
    }

    // Priority 3: Full name or reverse full name starts with query
    if (fullName.startsWith(query) || reverseFullName.startsWith(query)) {
      return 3;
    }

    // Check race (exact match, starts with, or word boundary match)
    if (student.race !== null && student.race !== undefined) {
      const raceString = (student.race === Race.Other
        ? (student.race_other_value || RaceDisplayedNames[Race.Other])
        : RaceDisplayedNames[student.race as Race]).toLowerCase();
      
      if (raceString === query) return 1;
      if (raceString.startsWith(query)) return 4;
      
      // Word boundary match
      const raceWords = raceString.split(/\s+/);
      for (const word of raceWords) {
        if (word.startsWith(query)) return 4;
      }
    } else if (query === 'n/a') {
      return 1;
    }

    // Check teachers
    if (student.teachers && student.teachers.length > 0) {
      for (const teacher of student.teachers) {
        const teacherFirstName = teacher.first_name?.toLowerCase().trim() || '';
        const teacherLastName = teacher.last_name?.toLowerCase().trim() || '';
        const teacherFullName = `${teacherFirstName} ${teacherLastName}`.trim();
        
        // Exact match
        if (teacherFullName === query || teacherFirstName === query || teacherLastName === query) {
          return 5;
        }
        
        // Starts with match
        if (teacherFirstName.startsWith(query) || teacherLastName.startsWith(query)) {
          return 6;
        }
        
        // Word boundary match for multi-word names (e.g., "Mary Jane" should match "Jane")
        const firstNameWords = teacherFirstName.split(/\s+/);
        for (const word of firstNameWords) {
          if (word === query) return 5;
          if (word.startsWith(query)) return 6;
        }
        
        const lastNameWords = teacherLastName.split(/\s+/);
        for (const word of lastNameWords) {
          if (word === query) return 5;
          if (word.startsWith(query)) return 6;
        }
        
        // Contains match (for partial matching within names)
        if (teacherFirstName.includes(query) || teacherLastName.includes(query)) {
          return 7;
        }
      }
    }

    return 999; // No match
  };

  // Optimized filter and sort using pre-built arrays
  getFilteredAndSortedStudents = (students: StudentInfo[], searchTerm: string, applySearchSort: boolean = true): StudentInfo[] => {
    if (!searchTerm) {
      return students;
    }

    const query = searchTerm.toLowerCase().trim();
    
    // If column sorting is active, only filter, don't sort by search relevance
    if (!applySearchSort) {
      return students.filter(student => {
        const priority = this.getMatchPriority(student, searchTerm);
        return priority < 999;
      });
    }
    
    // Build search arrays for efficient matching
    const firstNameMatches: StudentInfo[] = [];
    const lastNameMatches: StudentInfo[] = [];
    const otherMatches: StudentInfo[] = [];

    for (const student of students) {
      const firstName = student.first_name?.toLowerCase().trim() || '';
      const lastName = student.last_name?.toLowerCase().trim() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const reverseFullName = `${lastName} ${firstName}`.trim();
      const studentId = student.unique_id?.toString().toLowerCase() || '';

      // Priority Group 1: First name matches (exact or starts with) or Student ID
      if (
        firstName === query ||
        firstName.startsWith(query) ||
        studentId === query
      ) {
        firstNameMatches.push(student);
        continue;
      }

      // Priority Group 2: Last name matches (exact or starts with)
      if (
        lastName === query ||
        lastName.startsWith(query)
      ) {
        lastNameMatches.push(student);
        continue;
      }

      // Priority Group 3: Full name or reverse full name matches
      if (
        fullName === query ||
        fullName.startsWith(query) ||
        reverseFullName === query ||
        reverseFullName.startsWith(query)
      ) {
        lastNameMatches.push(student);
        continue;
      }

      // Priority Group 4: Race, teachers, etc
      const priority = this.getMatchPriority(student, searchTerm);
      if (priority < 999) {
        otherMatches.push(student);
      }
    }

    // Sort within each group by exact match first, then alphabetically
    const sortGroup = (group: StudentInfo[]) => {
      return group.sort((a, b) => {
        const aPriority = this.getMatchPriority(a, searchTerm);
        const bPriority = this.getMatchPriority(b, searchTerm);
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // If same priority, sort by name
        const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    };

    // Combine results: FIRST NAME matches first, then last name, then others
    return [
      ...sortGroup(firstNameMatches),
      ...sortGroup(lastNameMatches),
      ...sortGroup(otherMatches)
    ];
  };

  // Optimized filter function
  studentsFilter = (student: StudentInfo) => {
    const searchTerm = this.props.studentsFilter.trim();
    
    if (!searchTerm) {
      return true;
    }

    const priority = this.getMatchPriority(student, searchTerm);
    return priority < 999;
  };

  // Sort students by match priority (uses getFilteredAndSortedStudents for better performance)
  sortStudentsBySearchRelevance = (students: StudentInfo[]): StudentInfo[] => {
    const searchTerm = this.props.studentsFilter.trim();
    // Check if column sorting is active - if so, only filter, don't sort by search relevance
    const hasColumnSort = this.state.sortField && this.state.sortOrder;
    return this.getFilteredAndSortedStudents(students, searchTerm, !hasColumnSort);
  };

  studentDisplayFilter = (student: StudentInfo) => {
    if (
      this.props.studentsDisplaySettings ===
      StudentsDisplaySettings.DISPLAY_ALL_STUDENTS
    ){
      return true;
    } else {
      return this.props.studentsByInterventionGroupRoster.includes(student.id);
    }

    // const teachersIds = this.props.teachersRoster.map(
    //   (teacherInfo) => teacherInfo.user.id
    // );
    // return teachersIds.some((teacherId) => {
    //   return student.teachers.some((teacher) => teacherId === teacher.id);
    // });
  };

  componentDidMount() {
    if(!(this.props.isLoading.getTeachers || this.props.teachersRoster.length)) {
      this.props.getTeachers();
    }
    if(!(this.props.isLoading.getStudentsByInterventionGroup || this.props.studentsByInterventionGroupRoster.length)) {
      this.props.showLoadingIndicator();
      this.props.getStudentsByInterventionGroup().then((studentsByInterventionGroupIds: number[]) => {
        if(!studentsByInterventionGroupIds.length) {
          this.props.changeStudentsDisplaySetting(
            StudentsDisplaySettings.DISPLAY_ALL_STUDENTS
          );
          this.props.hideLoadingIndicator();
        }
        const students = this.props.studentsRoster.filter(
          this.studentDisplayFilter
        );
        if (students.length > 0) {
          const filteredStudents = students.filter(this.studentsFilter);
          const sortedStudents = this.sortStudentsBySearchRelevance(filteredStudents);
          this.setState({
            sortedStudentsRoster: sortedStudents,
          }, () => this.props.hideLoadingIndicator());
        }
      });
    }
  }

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any
  ): void {
    if (
      prevProps.isLoading.addStudents &&
      !this.props.isLoading.addStudents &&
      !this.props.errors.addStudents &&
      !this.props.existingStudents.length
    ) {
      this.props.getStudents();
    }
    if ((this.props.studentsByInterventionGroupRoster.length 
      && (prevProps.studentsByInterventionGroupRoster 
        !== this.props.studentsByInterventionGroupRoster)) ||
        (prevProps.isLoading.updateStudentDetails && 
        !this.props.isLoading.updateStudentDetails &&
        !this.props.errors.updateStudentDetails)) 
    {
      this.props.showLoadingIndicator();
      const students = this.props.studentsRoster.filter(
        this.studentDisplayFilter
      );
      const classListAsc = (document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>)?.[0];
      if(classListAsc){
        classListAsc.style.display = 'none';
      }
      if (students.length > 0) {
        const filteredStudents = students.filter(this.studentsFilter);
        const sortedStudents = this.sortStudentsBySearchRelevance(filteredStudents);
        this.setState({
          sortedStudentsRoster: sortedStudents,
        }, () => this.props.hideLoadingIndicator());
      } else {
        this.props.changeStudentsDisplaySetting(
          StudentsDisplaySettings.DISPLAY_ONLY_MY_STUDENTS
        );
      }
    }
    if (
      prevState.sortOrder !== this.state.sortOrder ||
      prevState.sortField !== this.state.sortField ||
      prevProps.studentsFilter !== this.props.studentsFilter ||
      prevProps.studentsDisplaySettings !== this.props.studentsDisplaySettings
      ) {
        if (
          this.props.studentsDisplaySettings ===
          StudentsDisplaySettings.DISPLAY_ONLY_MY_STUDENTS
          ) {
            const baseStudents = (prevState.sortOrder !== this.state.sortOrder ||
                                  prevState.sortField !== this.state.sortField)  
                                  ? this.state.sortedStudentsRoster
                                  : this.props.studentsRoster
                                    .filter(this.studentDisplayFilter)
                                    .filter(this.studentsFilter);
            const sortedStudents = this.sortStudentsBySearchRelevance(baseStudents);
            this.setState((state) => ({
              ...state,
              sortedStudentsRoster: sortedStudents,
            }), () => this.props.hideLoadingIndicator());
          } else {
            const baseStudents = (prevState.sortOrder !== this.state.sortOrder ||
                                  prevState.sortField !== this.state.sortField)  
                                  ? this.state.sortedStudentsRoster
                                  : this.props.studentsRoster
                                    .filter(this.studentsFilter);
            const sortedStudents = this.sortStudentsBySearchRelevance(baseStudents);
            this.setState((state, props) => ({
              ...state,
              sortedStudentsRoster: sortedStudents,
            }), () => this.props.hideLoadingIndicator());
          }
          
    }
  }

  // handleCheckStudent = (student: StudentInfo) => {
  //   const selectedStudents = this.state.selectedStudents;
  //   if (selectedStudents.includes(student)) {
  //     this.setState({
  //       selectedStudents: selectedStudents.filter((st) => st !== student),
  //     });
  //   } else {
  //     selectedStudents.push(student);
  //     this.setState({ selectedStudents: selectedStudents });
  //   }
  // };

  onStudentDetails = (student: StudentInfo) => {
    if (student.unique_id) {
      this.props.push(`${this.props.isDistrictOwner ? '/district' : ''}/rosters/students/${student.unique_id}?tab=active`);
    }
  };

  handleShowCreateClassModal = () => {
    this.setState({ showCreateClassModal: true });
  };

  handleHideCreateClassModal = () => {
    this.setState({ showCreateClassModal: false });
  };

  handleChangeSelectedTeacher = (teacher: ValueType<UserInfo, false>) => {
    this.setState((s) => ({ ...s, selectedTeacher: teacher as UserInfo }));
  };

  // handleAssignStudentToTeacher = () => {
  //   const { selectedTeacher, selectedStudents } = this.state;
  //   if (selectedTeacher) {
  //     this.props
  //       .assignStudentsToTeacher(
  //         selectedTeacher.id,
  //         selectedStudents,
  //         selectedTeacher
  //       )
  //       .then(
  //         () => {
  //           this.handleUnselectStudents();
  //         },
  //         (err: any) => {
  //           return  (err.response.status == 400) ?
  //                 toastr.error("Failed to assign the students to the teacher", 
  //                 (JSON.stringify(err.response.data).includes('last_name') 
  //                   || JSON.stringify(err.response.data).includes('first_name'))
  //                   ? `First name and Last name can not be blank, 
  //                   Please check the selected student(s).` : err.message)
  //               : err.message;
  //         }
  //       );
  //   }
  // };

  // handleUnselectStudents = () => {
  //   this.setState((s) => ({
  //     ...s,
  //     selectedStudents: [],
  //     selectedTeacher: undefined,
  //   }));
  // };

  handleTableChange = (type: TableChangeType, { sortField }: TableChangeState<any>) => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    if (type === 'sort') {
      let newSortField: string | undefined = sortField;
      let newSortOrder: 'asc' | 'desc' | undefined = 'asc';
      if (this.state.sortField === sortField) {
        if (this.state.sortOrder === 'asc') {
          newSortOrder = 'desc';
        } else if (this.state.sortOrder === 'desc') {
          // Third click → Clear the sort
          newSortField = undefined;
          newSortOrder = undefined;
        }
      }
      
      // IMPORTANT: Sort the ALREADY FILTERED data (respect intervention group toggle and search filter)
      // Start with the current filtered students
      let baseStudents = this.props.studentsRoster
        .filter(this.studentDisplayFilter)
        .filter(this.studentsFilter);
      
      if (newSortField && newSortOrder) {
        baseStudents.sort((a, b) => {
          let aValue, bValue;
        
          if (newSortField === 'race') {
            const getRaceString = (student: StudentInfo) => {
              if (student.race === null || student.race === undefined) return 'N/A';
              return student.race === Race.Other
                ? student.race_other_value || RaceDisplayedNames[Race.Other]
                : RaceDisplayedNames[student.race as Race];
            };
            aValue = getRaceString(a);
            bValue = getRaceString(b);
          } else if (newSortField === 'teachers') {
            const getTeachersString = (student: StudentInfo) =>
              student.teachers
                .sort((a: BasicUserInfo, b: BasicUserInfo) =>
                  collator.compare(
                    `${a.first_name} ${a.last_name}`,
                    `${b.first_name} ${b.last_name}`
                  )
                )
                .map((teacher: BasicUserInfo) => `${teacher.first_name} ${teacher.last_name}`)
                .join(', ');
            aValue = getTeachersString(a);
            bValue = getTeachersString(b);
          } else {
            aValue = (a as any)[newSortField!];
            bValue = (b as any)[newSortField!];
          }
        
          const isEmpty = (val: any) => val === null || val === undefined || val === 'N/A';
        
          if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
          if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
          if (isEmpty(aValue) && isEmpty(bValue)) return 0;
          
          aValue = aValue.toString().trim();
          bValue = bValue.toString().trim();
          
          return newSortOrder === "asc"
          ? collator.compare(aValue, bValue)
          : collator.compare(bValue, aValue);
        });
      }
      
      this.setState({
        sortField: newSortField,
        sortOrder: newSortOrder,
        sortedStudentsRoster: baseStudents,
        columns: this.getColumns(newSortField, newSortOrder),  // Update columns dynamically
      });
    }
  };

  render() {
    const {
      selectedTeacher,
      sortedStudentsRoster,
      //selectedStudents,
      showCreateClassModal,
    } = this.state;
    const isEmpty =
      sortedStudentsRoster.length === 0 && !this.props.isLoading.getStudents;
    
      const classListDesc = (document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>)?.[0];
      const classListAsc = (document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>)?.[0];
      if(this.state.sortOrder === 'asc') { 
        if(classListAsc){
          classListAsc.style.display = 'inline-block';
        }
      } else if (this.state.sortOrder === 'desc') {
        if(classListDesc){
          classListDesc.style.display = 'inline-block';
        }
      } else if(!this.state.sortField && !this.state.sortOrder) {
        if(classListAsc){
          classListAsc.style.display = 'none';
        }
        if(classListDesc){
          classListDesc.style.display = 'none';
        }
      }
    return (
      <>
        {/* {!!selectedStudents.length && (
              <div className="d-flex mt-2">
                <div style={{ minWidth: "250px" }}>
                  <Select
                    className="mr-3"
                    isLoading={
                      this.props.isLoading.getTeachers &&
                      !this.props.teachersRoster.length
                    }
                    isClearable
                    placeholder="Select teacher..."
                    value={selectedTeacher}
                    getOptionValue={(u) => u.id.toString()}
                    getOptionLabel={(u) => getFullName(u)}
                    onChange={this.handleChangeSelectedTeacher}
                    options={this.props.teachersRoster.map((tr) => tr.user)}
                  />
                </div>

                <button
                  className="blueBtnSm"
                  disabled={!selectedTeacher}
                  onClick={this.handleAssignStudentToTeacher}
                >
                  Assign to teacher{" "}
                  {this.props.isLoading.assignStudentsToTeacher.includes(
                    selectedTeacher?.id!
                  ) && <Spinner animation="border" size="sm" />}
                </button>

                <button
                  className="whiteBtnSm ml-3"
                  onClick={this.handleUnselectStudents}
                >
                  Unselect Students
                </button>
              </div>
            )} */}
        {this.props.isLoading.isLoadingIndicator
        && !this.props.errors.uploadStudentDemographics
        && !this.props.errors.addStudents
        && !this.props.errors.getStudents
        && !this.props.errors.updateStudentDetails

          ? <LoadingIndicator type="table" rows={20} columns={6} />
          : 
        <BootstrapTable
          // id="rosterTableID"
          remote={{ sort: true }}
          classes="rosterTable"
          wrapperClasses={sortedStudentsRoster.length === 0 ? "rosterTableEmpty" : ""}
          keyField="id"
          bordered={false}
          bootstrap4
          data={sortedStudentsRoster.map((student) => ({
            id: student.id,
            // checkbox: (
            //   <div>
            //     {(this.props.currentRole != UserAssignmentRole.TEACHER) && (
            //       <input
            //         type="checkbox"
            //         checked={selectedStudents.some((st) => st.id === student.id)}
            //         onChange={() => this.handleCheckStudent(student)}
            //       />
            //     )}
            //   </div>
            // ),
            first_name: student.first_name,
            last_name: student.last_name,
            race:
              student.race !== null && student.race !== undefined
                ? student.race === Race.Other
                  ? student.race_other_value || RaceDisplayedNames[Race.Other]
                  : RaceDisplayedNames[student.race]
                : "N/A",
            unique_id: student.unique_id,
            teachers: student.teachers
              .map((teacher) => `${teacher.first_name} ${teacher.last_name}`)
              .join(", "),
            actions: (
              <div className="d-flex justify-content-center">
                <span
                  className="mr-2"
                  onClick={() => {
                    this.props.openStudentEditModal(student);
                  }}
                >
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="edit-student-btn" className="customInfoTooltip">
                        Edit Student
                      </Tooltip>
                    }
                  >
                  <FontAwesomeIcon icon={faPen} size="lg"/>
                  </OverlayTrigger>
                </span>

                <OverlayTrigger
                  overlay={
                    <Tooltip id="studentDetails" className="customInfoTooltip">
                      Student Group Details
                    </Tooltip>
                  }
                >
                  <span onClick={() => this.onStudentDetails(student)}>
                    <FontAwesomeIcon icon={faChevronRight} size="lg"/>
                  </span>
                </OverlayTrigger>
              </div>
            ),
          }))}
          columns={this.state.columns}  
          pagination={sortedStudentsRoster.length > 20 ? paginationFactory(options) : undefined}
          // defaultSorted={defaultSorted}
          noDataIndication={
            isEmpty ? "No matching students" : <LoadingIndicator type="list" rows={20} columns={6} />
          }
          sort={this.state.sortField && this.state.sortOrder ? {
            dataField: this.state.sortField,
            order: this.state.sortOrder,
          } : undefined }   
          onTableChange={this.handleTableChange}
        />
        }
      </>
    );
  }
}

const mapStateToProps = ({ onboarding, auth, cases }: ApplicationState): PropsFromState => {
  return {
    studentsRoster: onboarding.studentsRoster,
    teachersRoster: onboarding.teachersRoster,
    studentsByInterventionGroupRoster: onboarding.studentsByInterventionGroupRoster,
    studentsFilter: onboarding.studentsFilter,
    studentsDisplaySettings: onboarding.studentsDisplaySettings,
    currentRole: auth.userInfo?.profile.current_role,
    errors: {
      getStudents: onboarding.errors.getStudents,
      addStudents: onboarding.errors.addStudents,
      updateStudentDetails: onboarding.errors.updateStudentDetails,
      uploadStudentDemographics: onboarding.errors.uploadStudentDemographics,
    },
    isLoading: {
      getStudents: onboarding.isLoading.getStudents,
      addStudents: onboarding.isLoading.addStudents,
      getTeachers: onboarding.isLoading.getTeachers,
      getStudentsByInterventionGroup: onboarding.isLoading.getStudentsByInterventionGroup,
      assignStudentsToTeacher: onboarding.isLoading.assignStudentsToTeacher,
      isLoadingIndicator: onboarding.isLoading.isLoadingIndicator,
      updateStudentDetails: onboarding.isLoading.updateStudentDetails
    },
    interventionGroups: cases.interventionGroups,
    existingStudents: onboarding.existingStudents!,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return bindActionCreators(
    {
      getStudents: getStudents,
      getTeachers: getTeachers,
      getStudentsByInterventionGroup: getStudentsByInterventionGroup,
      changeStudentsDisplaySetting: changeStudentsDisplaySetting,
      //assignStudentsToTeacher: assignStudentsToTeacher,
      openStudentEditModal: openStudentEditModal,
      showLoadingIndicator: showLoadingIndicator,
      hideLoadingIndicator: hideLoadingIndicator,
      push: push,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(withMyHook(StudentsTable));
